/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import {
  useRef,
  useCallback,
  useState,
  useImperativeHandle,
  useEffect,
  useMemo,
  useContext,
  useLayoutEffect
} from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { containsKey, Keys, isKeyDefined, addKeys } from '../utils/UNSAFE_keys';
import { getItemContext, getPrevNextKey } from '../utils/PRIVATE_collectionUtils';
import { DataState, ItemContext, ListItemRendererContext, Range } from '../UNSAFE_Collection';
import { LoadMoreCollection, LOADMORE_STYLE_CLASS } from '../PRIVATE_LoadMoreCollection';
import { VirtualizedCollection } from '../PRIVATE_VirtualizedCollection';
import type { UnpackSignals } from '../utils/UNSAFE_attributeUtils';
import { ListViewItem, MemoizeListViewItem } from './ListItem';
import { getListViewRoles } from './ListRoles';
import { isMultipleSelection, getViewportConfig, LIST_ITEM_SELECTOR } from './ListUtils';

import { SelectionRangeDetail } from '../hooks/PRIVATE_useSelection';
import { SkeletonContainer } from './SkeletonContainer';
import { Skeleton } from '../UNSAFE_Skeleton';
import { Flex } from '../UNSAFE_Flex';
import { Props } from '../UNSAFE_ListView';
import { Sparkle } from './Sparkle';
import { listStyles } from './themes/ListStyles.css';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { useNavigation } from '../hooks/PRIVATE_useListCommon';
import { useCollectionInteractionContext } from '../hooks/UNSAFE_useCollectionInteractionContext';
import { usePromotedSection } from './usePromotedSection';
import { useSelectionItemAction, handleSelectionRange } from './useSelectionItemAction';
import { Inset } from '../UNSAFE_Inset';
// TODO: add this import due to issue JET-67485, need to update once Stable is introduced
import './themes/redwood/ListBaseTheme.css';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import { setScrollPosition, ListViewScrollPosition } from '#UNSAFE_ListView/ScrollPositionUtils';
import { getClientHints } from '#utils/PRIVATE_clientHints';

/**
 * Component that renders items as a flat list.
 * In order to maximize performance, only items that are visible in the viewport are rendered.
 */
export function List<K extends string | number, D>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  allowTabbableMode = true,
  children,
  data,
  gridlines,
  // JET-75866 - dropdown should only show three skeletons on initial display with collectionTemplate
  // We will determine which loading indicator we need to use based on whether the List is rendered in embedded
  // mode or not.
  loadingIndicator: propLoadingIndicator,
  loadMoreIndicator = defaultLoadMoreIndicator,
  currentItemOverride,
  onPersistCurrentItem,
  onLoadRange,
  onSelectionChange,
  onItemAction,
  rangeExtractor,
  role = 'grid',
  selectedKeys = emptyKeys as Keys<K>,
  selectionMode = 'none',
  viewportConfig,
  currentItemVariant,
  promotedSection,
  isVirtualized = false,
  customItemRenderer,
  scrollToVisibleOffset,
  testId,
  scrollerRef,
  reorderProps,
  contextMenuTriggerProps,
  itemPadding,
  itemEnterKeyFocusBehavior = 'focusWithin',
  ...props
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const isFetching = useRef(false);
  const scrollPositionRef = useRef<ListViewScrollPosition<K>>();

  // tracking pending selection key that is not in current data
  const pendingSelection = useRef<{ detail: SelectionRangeDetail<K>; range: Range }>();

  const [active, setActive] = useState<boolean>(false);

  const showGridline = (index: number) => {
    // show bottom gridlines for each item, and for the last item if specified
    return (
      gridlines?.item === 'visible' &&
      (index + 1 !== data?.totalSize || gridlines?.bottom === 'visible')
    );
  };

  const showGridlineTop = () => {
    // show the top gridline for the first item
    return gridlines?.item === 'visible' && gridlines?.top === 'visible';
  };

  const ariaMultiSelectable =
    selectionMode === 'none' ? undefined : isMultipleSelection(selectionMode);
  const ariaRowCount =
    data === null ? undefined : data.sizePrecision === 'exact' ? data.totalSize : -1;

  const allowBrowserContextMenu = !(
    getClientHints().platform === 'ios' || getClientHints().platform === 'android'
  );

  const classArray = [listStyles.base];
  if (!allowBrowserContextMenu) {
    classArray.push(listStyles.userSelect);
  }
  const classes = classNames(classArray);

  const { isTabbable } = useContext(TabbableModeContext);

  const isEmbedded = useCollectionInteractionContext() === 'embedded';
  const itemVariant =
    currentItemVariant !== undefined ? currentItemVariant : isEmbedded ? 'highlight' : 'none';
  // JET-75866 - dropdown should only show three skeletons on initial display with collectionTemplate
  // When rendering in embedded mode, the specs need to show only 3 skeletons. So when there is no custom
  // loading indicator and in embedded mode, we use the 3 skeleton loading indicator.
  const loadingIndicator =
    propLoadingIndicator ??
    (isEmbedded ? defaultEmbeddedLoadingIndicator : defaultLoadingIndicator);

  const pendingSelectionCallback = useCallback(
    (detail: SelectionRangeDetail<K>, value: Range) => {
      if (data) {
        pendingSelection.current = {
          detail: detail,
          range: { offset: data.offset, count: data.data.length }
        };
        onLoadRange(value);
      }
    },
    [data, onLoadRange]
  );

  viewportConfig = getViewportConfig(rootRef, viewportConfig);

  /*****************************************************************************************/
  /********************************** Scroll Position **************************************/
  /*****************************************************************************************/

  // update scroll position if a new value is provided
  useLayoutEffect(() => {
    if (rootRef.current && scrollPositionRef.current !== props.scrollPositionOverride) {
      setScrollPosition(rootRef.current, props.scrollPositionOverride);
      scrollPositionRef.current = props.scrollPositionOverride;
    }
  }, [props.scrollPositionOverride]);

  /*****************************************************************************************/
  /************************************* Load More *****************************************/
  /*****************************************************************************************/

  const lastRange = useRef({ offset: 0, count: 0 });
  const handleLoadRange = useCallback(
    (newRange: { offset: number; count: number }) => {
      // check isFetching to avoid multiple load more at the same time (details in JET-68994)
      if (
        !isFetching.current ||
        lastRange.current.offset !== newRange.offset ||
        lastRange.current.count !== newRange.count
      ) {
        isFetching.current = true;
        lastRange.current = newRange;
        onLoadRange(newRange);
      }
    },
    [isFetching, onLoadRange]
  );

  useEffect(() => {
    isFetching.current = false;
    // listen to data because when one of them gets updated, the fetching is done
  }, [data]);

  const listRoles = useMemo(() => getListViewRoles(role), [role]);

  const arrowKeyAndHandlers = {
    ArrowUp: (key?: K) => getPrevNextKey(rootRef.current, key, true, LIST_ITEM_SELECTOR),
    ArrowDown: (key?: K) => getPrevNextKey(rootRef.current, key, false, LIST_ITEM_SELECTOR)
  };

  const { navigationProps, currentKey, showFocusRing, isCurrentTabbableKey } = useNavigation(
    data,
    rootRef,
    (elem: Element) => {
      return idExtractor(elem, listRoles.cell);
    },
    LIST_ITEM_SELECTOR,
    arrowKeyAndHandlers,
    currentItemOverride,
    onPersistCurrentItem,
    viewportConfig,
    scrollToVisibleOffset,
    selectionMode !== 'multiple' || isEmbedded,
    allowTabbableMode,
    true,
    isEmbedded,
    itemEnterKeyFocusBehavior === 'focusWithin'
  );

  const { eventProps, handleSelectionChange } = useSelectionItemAction(
    currentKey,
    selectedKeys,
    selectionMode,
    onSelectionChange,
    onItemAction,
    data,
    viewportConfig,
    scrollToVisibleOffset,
    rootRef,
    pendingSelectionCallback,
    isEmbedded
  );

  if (data && pendingSelection.current !== undefined && onSelectionChange) {
    const keys = handleSelectionRange(pendingSelection.current.detail, data);
    if (Array.isArray(keys)) {
      if (!isEmbedded) {
        onSelectionChange({ value: { all: false, keys: new Set(keys) }, target: null });
      } else {
        const value = addKeys(selectedKeys, keys);
        onSelectionChange({ value, target: null });
      }
    }
    const range = pendingSelection.current
      ? pendingSelection.current.range
      : { offset: data.offset, count: data.data.length };
    data = adjustDataState(data, range);
    pendingSelection.current = undefined;
    onLoadRange(range);
  }

  const { sparkleHeight, suggestions } = usePromotedSection(data, promotedSection, rootRef);

  // TODO: check suggestions before Sparkle would cause a scrolling issue
  const sparkleIndicator = <Sparkle sparkleHeight={sparkleHeight}></Sparkle>;

  // Collection generic is of this type
  type DataStateData = DataState<K, D>['data'][number];

  const ItemComponent = isVirtualized ? ListViewItem : MemoizeListViewItem;

  const childrenComponent = (context: ItemContext<DataStateData>) => {
    const listItemContext = getItemContext<K, D>(
      context,
      selectedKeys,
      isMultipleSelection(selectionMode),
      handleSelectionChange
    );
    const suggestion = suggestions?.get(listItemContext.metadata.key);
    const isTabbable = allowTabbableMode && isCurrentTabbableKey(listItemContext.metadata.key);
    const isFocused = currentKey === listItemContext.metadata.key && !isTabbable;
    const isActive = isFocused && active;
    const isSelected = containsKey(selectedKeys, listItemContext.metadata.key);
    const isFocusRingVisible = isFocused && showFocusRing;
    const listItemFunc = (ctx: ListItemRendererContext<K, D>, otherProps?: any) => (
      <ItemComponent
        key={ctx.metadata.key}
        context={customItemRenderer ? ctx : undefined}
        itemData={ctx.data}
        itemKey={ctx.metadata.key}
        itemMetadata={ctx.metadata}
        itemIndex={ctx.index}
        isFocused={isFocused}
        isFocusRingVisible={isFocusRingVisible}
        isActive={isActive}
        isGridlineVisible={showGridline(ctx.index)}
        selectedKeys={selectedKeys}
        isTabbable={allowTabbableMode && isCurrentTabbableKey(ctx.metadata.key)}
        role={role}
        selectionMode={selectionMode}
        handleSelectionChange={handleSelectionChange}
        currentItemVariant={itemVariant}
        padding={itemPadding}
        {...(suggestion && { suggestion })}
        {...(ctx.index === 0 && { isTopGridlineVisible: showGridlineTop() })}
        {...otherProps}>
        {children}
      </ItemComponent>
    );

    // if customItemRenderer is specified (ExpandableList and GroupedList)
    // create a custom context with additional info including a function
    // that helps create the default item component
    if (customItemRenderer) {
      const customRendererContext = {
        listItemContext,
        isFocused,
        isFocusRingVisible,
        isSelected,
        isTabbable,
        isActive,
        defaultListItem: listItemFunc
      };
      return customItemRenderer(customRendererContext);
    }
    return listItemFunc(listItemContext);
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === ' ' && isKeyDefined(currentKey) && selectionMode !== 'none') {
        setActive(true);
      }
    },
    [currentKey, selectionMode, setActive]
  );

  const handleKeyUp = useCallback(() => {
    setActive(false);
  }, [setActive]);

  useImperativeHandle(scrollerRef!, () => rootRef.current);

  // if data is not specified, listview should show loading indicator
  // todo: replace placeholder with actual SkeletonContainer component (Ash is working on)
  const testIdProps = useTestId(testId);
  return (
    <div
      {...mergeProps(
        eventProps,
        navigationProps,
        contextMenuTriggerProps || {},
        reorderProps || {},
        {
          onKeyDown: handleKeyDown,
          onKeyUp: handleKeyUp
        },
        props
      )}
      {...testIdProps}
      role={role}
      aria-rowcount={role === 'grid' ? ariaRowCount : undefined}
      aria-colcount={role === 'grid' ? 1 : undefined}
      ref={rootRef}
      class={classes}
      tabIndex={isTabbable ? 0 : -1}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-multiselectable={ariaMultiSelectable}>
      {data == null ||
      (data.data.length === 0 && data.sizePrecision === 'atLeast' && data.totalSize > 0) ? (
        loadingIndicator
      ) : isVirtualized ? (
        <VirtualizedCollection
          data={data}
          itemSelector={LIST_ITEM_SELECTOR}
          loadMoreIndicator={loadMoreIndicator}
          onLoadRange={onLoadRange}
          rangeExtractor={rangeExtractor}
          suggestions={sparkleIndicator}
          viewportConfig={viewportConfig}>
          {childrenComponent}
        </VirtualizedCollection>
      ) : (
        <LoadMoreCollection
          data={data.data}
          loadMoreIndicator={loadMoreIndicator}
          hasMore={data.sizePrecision === 'atLeast' && data.data.length <= data.totalSize}
          onLoadMore={() => {
            data && handleLoadRange({ offset: 0, count: data.data.length + 25 });
          }}
          suggestions={sparkleIndicator}
          viewportConfig={viewportConfig}>
          {childrenComponent}
        </LoadMoreCollection>
      )}
    </div>
  );
}

const idExtractor = (elem: Element, role: UnpackSignals<JSX.HTMLAttributes['role']>) => {
  return elem.querySelector(`[role=${role}]`)?.id || '';
};

const emptyKeys = { all: false, keys: new Set() };

const defaultLoadingIndicator = (
  <SkeletonContainer minimumCount={25}>
    {() => {
      return (
        <Inset variant="listview">
          <Flex height="6x" align="center">
            <Skeleton height="4x" />
          </Flex>
        </Inset>
      );
    }}
  </SkeletonContainer>
);

const defaultEmbeddedLoadingIndicator = (
  <SkeletonContainer minimumCount={3}>
    {() => {
      return (
        <Inset variant="listview">
          <Flex height="6x" align="center">
            <Skeleton height="4x" />
          </Flex>
        </Inset>
      );
    }}
  </SkeletonContainer>
);

const defaultLoadMoreIndicator = (
  <div class={LOADMORE_STYLE_CLASS}>
    <SkeletonContainer minimumCount={3}>
      {() => {
        return (
          <Inset variant="listview">
            <Flex height="6x" align="center">
              <Skeleton height="4x" />
            </Flex>
          </Inset>
        );
      }}
    </SkeletonContainer>
  </div>
);

/**
 * Adjust the DataState as needed if it contains more than needed for the specified range
 */
const adjustDataState = <K, D>(dataState: DataState<K, D>, range: Range | null) => {
  if (range) {
    // prevent range offset from being larger than the data state
    const safeRangeOffset = Math.min(range.offset, dataState.offset + dataState.totalSize);
    const diff = safeRangeOffset - dataState.offset;
    if (diff > 0) {
      dataState = {
        offset: safeRangeOffset,
        data: dataState.data.slice(diff, diff + range.count),
        totalSize: dataState.totalSize,
        sizePrecision: dataState.sizePrecision
      };
    }
  }
  return dataState;
};
